/**
 * WorldConfig — the per-world configuration contract for the agnostic
 * multi-setting platform (ADR 0007 / 0009).
 *
 * A "world" (grymwyrd.com, zweihander.games, …) is a branded site that
 * surfaces one or more `game_system`s + `setting`s served by the shared api, and
 * gates its first-party content behind books. Everything world-specific that the
 * shared shell needs lives here as DATA, not as hardcoded literals — so the same
 * encyclopedia / entitlement / facet code serves every world.
 *
 * The active config is a process singleton, registered once at bootstrap
 * (`setActiveWorldConfig` in the app's `main.tsx`, and in test setup). The shared
 * modules (`lib/api/rpgApi`, `lib/entitlements/contentAccess`) read it lazily
 * inside their functions via {@link getActiveWorldConfig} — never at module load —
 * so import order is irrelevant.
 *
 * Destined for `packages/@gamecodex/world-config` in P2; keep it dependency-free.
 */

/** A selectable encyclopedia facet value (game system / setting). */
export interface FacetOption {
  /** Filter value sent to the API (stable slug/code). */
  id: string
  label: string
  /**
   * Whether this facet has live content the API can actually serve/filter today.
   * Selecting a non-live facet yields an honest empty result rather than leaking
   * content from another system/setting (see `isLiveSystem`/`isLiveSetting`).
   */
  available?: boolean
  /**
   * (Game-system facets only.) When true, this system's content lives in the
   * api's agnostic `system_entity` store and is served by the `/system-entities`
   * endpoint family (`?game_system=<code>&type=<t>&locale=` list + `/:id` detail)
   * rather than the typed-5e `/entities/<type>` endpoints. The encyclopedia
   * api-client (`lib/api/rpgApi`) branches on this to choose the right endpoint
   * and normalizes both response shapes to the same `EntityListResponse`/
   * `EntityRef` (so the rendered `html` field renders identically). False/absent
   * = the legacy typed `/entities` path (5e / Grymwyrd). Set on `zweihander-reforged`.
   */
  usesSystemEntities?: boolean
}

// ── i18n: locales + content catalog ─────────────────────────────────────────

/** A locale this world supports. `code` is a BCP-47 tag used in URLs (`/en`, `/de`). */
export interface LocaleDef {
  /** URL/registry code, e.g. `en`, `de`. */
  code: string
  /** English label, e.g. `English`, `German`. */
  label: string
  /** Native label for the switcher, e.g. `Deutsch`. */
  native: string
  /** Text direction. */
  dir: 'ltr' | 'rtl'
  /** When true, the locale is offered + fully translated but its content is
   *  license-restricted: visiting `/{code}/…` renders a "coming soon — license
   *  restricted" gate (footer only, no nav) instead of the app. */
  restricted?: boolean
}

/** Which locales a world offers + its default (the locale `/` redirects to). */
export interface LocalesConfig {
  /** Default locale code; bare `/` redirects to `/${default}`. */
  default: string
  /** Every supported locale (must include `default`). */
  supported: LocaleDef[]
}

/**
 * A flat, namespaced message catalog for ONE locale. Keys are dotted
 * (`home.heroTitle`, `nav.encyclopedia`); values may contain `{var}`
 * placeholders interpolated by `useT`. This is BOTH the per-world copy AND the
 * translation catalog — agent-generated per world per locale.
 */
export type MessageCatalog = Record<string, string>

/** All locales' catalogs for a world, keyed by locale code. */
export type WorldContent = Record<string, MessageCatalog>

// ── Starting avatars: in-world headshots offered to new players ──────────────

/** A lineage / species filter for the starting-avatar picker. */
export interface StartingAvatarLineage {
  id: string
  /**
   * In-world lineage name shown on the filter chip. Lineage names, blurbs and
   * group names are setting CONTENT: they keep the setting's own voice and
   * spelling and are not run through the UI string catalog.
   */
  label: string
  /** Optional one-line lore description shown under the chips when selected (content). */
  blurb?: string
  /**
   * Optional group heading (e.g. a people or kind). Lineages sharing a group are
   * shown together under it; lineages without one keep the flat chip row.
   */
  group?: string
}

/** One in-world headshot a player may start with. */
export interface StartingAvatar {
  id: string
  /** {@link StartingAvatarLineage.id} this portrait belongs to. */
  lineageId: string
  /** Alt text / tooltip. */
  label: string
  /** Absolute URL or /public path to a square headshot. */
  url: string
}

/**
 * In-world starting portraits. When set, players with no avatar (or the old
 * generated placeholder) get one of these instead, and the avatar editor shows
 * a lineage-filtered picker. Worlds that leave this unset keep the previous
 * generated-avatar behavior.
 */
export interface StartingAvatarsConfig {
  /**
   * @deprecated UI copy. The picker heading, hint and "all" chip read from the
   * world's message catalog (`dashboard.avatarEditor.starting.*`) so they are
   * localized with the rest of the UI. Kept optional for older configs.
   */
  heading?: string
  /** @deprecated See {@link StartingAvatarsConfig.heading}. */
  hint?: string
  /** @deprecated See {@link StartingAvatarsConfig.heading}. */
  allLabel?: string
  lineages: StartingAvatarLineage[]
  portraits: StartingAvatar[]
}

// ── Branding: image palette + fonts + animated background ────────────────────

/**
 * Per-world visual identity (locale-independent). Replaces the hardcoded
 * Grymwyrd logo/textures/fonts/background in the shared shell.
 */
export interface WorldBranding {
  /** Image "slots" the shell references — absolute URLs or /public paths. */
  images: {
    /** Header/footer/auth wordmark logo. */
    logo: string
    /** Default social card. */
    ogImage: string
    /** Browser favicon. */
    favicon: string
    /** Fallback user avatar. */
    avatarFallback: string
    /** Card-surface parchment texture (tailwind `bg-parchment`). */
    parchmentTexture: string
    /** Accent splatter texture (tailwind `bg-blood-splatter`). */
    splatterTexture: string
  }
  /** Display/heading/body font families (the `ui` font stays fixed for a11y). */
  fonts: {
    /** Big wordmark/hero font (the "Enter the Twilight" treatment). */
    display: string
    heading: string
    body: string
    /** Google Fonts (or other) stylesheet URL to load these families. */
    stylesheetUrl: string
  }
  /** Pluggable animated landing background, keyed by a registered variant. */
  background: {
    /** Variant key resolved by the shell's BackgroundLayer registry. */
    variant: string
    /** Variant-specific params (gradient stops, mist timing, particle count/color…). */
    params?: Record<string, unknown>
  }
}

export interface WorldConfig {
  /** Stable world slug (e.g. `grymwyrd`, `zweihander`). */
  worldId: string
  /** Display name (e.g. `Grymwyrd`). */
  worldName: string
  /**
   * Name for the first-party content this portal authors, used in legal copy
   * (AI Policy, intellectual-property, and canon-license clauses) wherever
   * ownership or authorship is asserted — distinct from {@link
   * WorldConfig.worldName} (the brand/site name in non-ownership references).
   *
   * Three IP/licensing cases drive how a world sets this:
   *  1. Licensed-in IP, we own our derivative work — name our content, not the
   *     licensed brand, so we don't imply we own that brand. The shared IP
   *     clause's "owned by the Publisher and its licensors" stays accurate.
   *     e.g. Zweihänder: `legalContentName: 'Our Powered By Zweihänder Content'`
   *     (brand "Zweihänder Reforged" is owned by SLLSWRD and licensed to us).
   *  2. Fully original, we own everything — leave unset; defaults to
   *     {@link WorldConfig.worldName}. e.g. Grymwyrd.
   *  3. We author under a third party's license and THEY own the result — the
   *     content is not ours, so the IP clause must attribute ownership to the
   *     licensor rather than the Publisher. NOT YET SUPPORTED: this needs an
   *     ownership-attribution override beyond just the name (see legalContent's
   *     "owned by the Publisher" wording). Add when the first such world lands
   *     (e.g. the upcoming Hannibal board-game portal).
   */
  legalContentName?: string
  /** Primary domain (e.g. `grymwyrd.com`). */
  domain: string

  /** Locales this world offers + its default (drives `/{locale}` routing + hreflang). */
  locales: LocalesConfig
  /** Localized copy catalogs, keyed by locale code (read via `useT`). */
  content: WorldContent
  /** Visual identity: image palette, fonts, animated background. */
  branding: WorldBranding
  /** Optional in-world starting headshots (see {@link StartingAvatarsConfig}). */
  startingAvatars?: StartingAvatarsConfig

  /** Game systems (rulesets) this world surfaces as encyclopedia facets. */
  gameSystems: FacetOption[]
  /** Settings (worlds/lore) this world surfaces as encyclopedia facets. */
  settings: FacetOption[]
  /** Default system facet selection, if any. */
  defaultSystem?: string
  /** Default setting facet selection, if any. */
  defaultSetting?: string

  /**
   * Optional crowdfunding promo rendered on the home page (the FeaturedBooks
   * teaser). Per-world so each portal promotes its OWN next book — omit to hide
   * the teaser entirely. Copy is English here (matches the rest of the teaser);
   * `slug` links to `/campaigns/:slug`.
   */
  homeCampaign?: {
    /** Campaign route slug → `/campaigns/{slug}`. */
    slug: string
    /** Small uppercase kicker above the title (e.g. "Now Crowdfunding"). */
    eyebrow: string
    /** Campaign / book title (e.g. "BrassLung Syndicate"). */
    title: string
    /** One- or two-sentence pitch. */
    body: string
    /** Primary button label (e.g. "Back BrassLung Syndicate"). */
    ctaLabel: string
    /** Optional logo/art shown in place of the default rocket icon. */
    image?: string
    /** Alt text for {@link image}; defaults to the title. */
    imageAlt?: string
  }

  // ── SEO / GEO defaults for the shared <Seo> head ─────────────────────────
  /**
   * Per-world SEO/GEO defaults. The shared `<Seo>` component composes titles,
   * canonical URLs (against {@link WorldConfig.domain}) and OG/Twitter tags from
   * these — so each world emits its OWN brand + canonical origin, never another
   * world's. (A canonical pointing at the wrong domain de-indexes the site.)
   */
  seo: {
    /** Meta description used when a page sets none. */
    defaultDescription: string
    /** Tagline composed into the home/fallback title: `${worldName} — ${tagline}`. */
    tagline: string
    /** Default OG/Twitter image path (resolved absolute against the world domain). */
    defaultOgImage: string
  }

  /**
   * The shared hex-tile-kit map builder (`/map-builder`, `HexBuilder.tsx`).
   * Set this to point the route at a real `/3d-kits/:id` catalog entry for this
   * world's book; omit it to show an honest "no kit configured" state instead
   * of a crash (same philosophy as `isLiveSystem`/`isLiveSetting`).
   */
  mapBuilder?: {
    /** The `pub_3d_kit` id to build with (api `GET /3d-kits/:id`). */
    kitId: string
  }

  /**
   * Eternal City: the living-city simulation (engine in `@gamecodex/eternal-city`, hosted by the
   * `eternal-city-worker`). Absent = this world has no eternal campaign and `/world/cities/*`
   * redirects home. A globe hex listed here offers "Enter the eternal city" beside the tabletop campaign.
   */
  eternalCity?: {
    /** Base URL of the eternal-city Worker, e.g. `https://city.gamecodex.com`. */
    serviceUrl: string
    /**
     * How the city map is drawn. `'three'` uses the 3D scene (fixed isometric camera, sun and shadows from the game
     * clock, night lights, quality tiers) and falls back to the 2D canvas on devices without WebGL2, after a lost GPU
     * context, or with `?renderer=2d`. Absent or `'2d'` keeps the 2D canvas.
     */
    renderer?: '2d' | 'three'
    cities: {
      /** City slug (the engine instance), e.g. `montkelian`. */
      slug: string
      /** Display name, shown on the hex dialog and the city page. */
      name: string
      /** The globe campaign-hex id this city sits on (see `buildCampaignHexes`). */
      hexId: string
      /** One line shown on the hex dialog. */
      blurb: string
    }[]
  }

  /**
   * This world's own `pub_setting` row id (a real DB UUID) — distinct from
   * `settings[].id`, which is a stable SLUG used as an api filter *value*
   * (e.g. `?setting=pixen-intrigue`) for entity endpoints. `/books` only
   * accepts `setting_id` as a UUID, not a slug, so this field exists purely
   * to let book-listing components (e.g. the shared home `LiveFeed`) scope
   * `useBooks({ setting_id: settingId })` to this world's own catalog. Omit
   * it (as every world does today except where explicitly set) to leave
   * book listings unscoped — the pre-2026-07-12 default, unchanged.
   */
  settingId?: string

  // ── Entitlement / content-access policy ──────────────────────────────────
  /** Game systems whose canon is openly licensed (never gated) in this world. */
  openCanonSystemIds: readonly string[]
  /** Settings that are first-party (paid) canon, gated behind a purchase. */
  lockedSettingIds: readonly string[]
  /** RPGPublisher book ids that unlock this world's first-party content. */
  unlockBookIds: readonly string[]
  /** Book to require when a locked selection names no specific unlock book. */
  fallbackUnlockBookId: string
}

let active: WorldConfig | null = null

/** Register the active world config. Call once at app bootstrap (and test setup). */
export function setActiveWorldConfig(config: WorldConfig): void {
  active = config
}

/** The active world config. Throws if bootstrap never registered one. */
export function getActiveWorldConfig(): WorldConfig {
  if (!active) {
    throw new Error(
      'WorldConfig not initialized — call setActiveWorldConfig() at app bootstrap (main.tsx) or in test setup.',
    )
  }
  return active
}

/** Whether a world config has been registered (handy in tests/guards). */
export function hasActiveWorldConfig(): boolean {
  return active !== null
}
